import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import TooltipTsoPq from '@components/Tooltip/TooltipTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { ReactElement } from 'react';
import { statusChipProps } from './StatusChipConfig';

type statusChip = {
  type?: keyof (typeof statusChipProps)['background'];
  label?: string;
  icon?: React.ReactNode;
  backgroundColor?: string;
  labelColor?: string;
  hoverColor?: string;
  tooltip?: string;
  tooltipMinWidth?: string;
};

const StyledIconWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: '0.75rem',

  '& svg': {
    fontSize: '0.625rem',
  },
});

const StyledChipWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    !['type', 'label', 'icon', 'backgroundColor', 'labelColor', 'hoverColor', 'tooltip', 'tooltipMinWidth'].includes(
      prop as string
    ),
})<statusChip>(({ backgroundColor, labelColor, type, hoverColor }) => {
  const bgColor =
    backgroundColor ??
    (type ? statusChipProps?.background[type] : tsoPqTheme.palette.customTextColor.color_font_ft_text);
  const textColor =
    labelColor ?? (type ? statusChipProps?.labelColor[type] : tsoPqTheme.palette.customTextColor.color_font_ft_text);
  const hovColor =
    hoverColor ??
    (type ? statusChipProps?.hoverColor[type] : tsoPqTheme.palette.stroke.color_neutral_stroke_str_ntrl_accent);

  return {
    borderRadius: tsoPqTheme.shape.border_radius_tso_border_radius_4,
    border: `0.0625rem solid ${bgColor}`,
    gap: tsoPqTheme.shape.border_radius_tso_border_radius_2,
    height: '1rem',
    minWidth: '1.5rem',
    minHeight: '1rem',
    display: 'inline-flex',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    color: textColor,
    padding: `${tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_2} ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_4}`,

    ':hover': {
      background: hovColor,
    },
  };
});

const StyledLabelWrapper = styled(TypographyTsoPq, {
  shouldForwardProp: (prop) => !['labelColor'].includes(prop as string),
})<statusChip>(({ labelColor, type }) => {
  const textColor =
    labelColor ?? (type ? statusChipProps?.labelColor[type] : tsoPqTheme.palette.customTextColor.color_font_ft_text);

  return {
    color: textColor,
    textAlign: 'center',
  };
});

export const StatusChipTsoPq = ({
  label,
  icon,
  backgroundColor,
  labelColor,
  hoverColor,
  tooltip,
  tooltipMinWidth,
  type,
}: statusChip): ReactElement => {
  const statusChip = (
    <StyledChipWrapper type={type} labelColor={labelColor} backgroundColor={backgroundColor} hoverColor={hoverColor}>
      {label && (
        <StyledLabelWrapper typographyType={'pageSubtitleSMBold'} type={type} labelColor={labelColor}>
          {label}
        </StyledLabelWrapper>
      )}
      {icon && <StyledIconWrapper>{icon}</StyledIconWrapper>}
    </StyledChipWrapper>
  );

  return tooltip ? (
    <TooltipTsoPq title={tooltip} customWidth={tooltipMinWidth}>
      {statusChip}
    </TooltipTsoPq>
  ) : (
    statusChip
  );
};
